// Which record the user is looking at, so the quick-message button can tag it.
//
// Two sources, the first one found wins:
//   1. A page that shows a record in a sidebar/modal (no URL of its own) sets it with
//      the mixin below while the sidebar is open.
//   2. Otherwise the current URL, matched against this repo's entityRoutes.js
//      (/claim/12 -> claim 12, /manage-drugs?focus=5 -> drug 5).
import chat from "./store";
import { SUPPORTED_TYPES, entityRoute } from "./entityRoutes";

const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Built once from entityRoutes.js: [{type, path: RegExp, focus: bool}]
let patterns = null;
function routePatterns() {
  if (patterns) return patterns;
  patterns = SUPPORTED_TYPES.map((type) => {
    const [path, query] = entityRoute(type, ":id").split("?");
    if (query) {
      // List page: the id is in ?focus=
      return { type, path: new RegExp(`^${escape(path)}/?$`), focus: true };
    }
    return { type, path: new RegExp(`^${escape(path).replace(":id", "(\\d+)")}/?$`), focus: false };
  });
  return patterns;
}

/** {type, id} for a route, or null. */
export function entityFromRoute(route) {
  if (!route) return null;
  for (const p of routePatterns()) {
    const m = p.path.exec(route.path);
    if (!m) continue;
    const id = p.focus ? route.query && route.query.focus : m[1];
    if (id && /^\d+$/.test(String(id))) return { type: p.type, id: Number(id) };
  }
  return null;
}

/** The record to tag on the current page, or null. */
export function currentContext(route) {
  return chat.state.pageContext || entityFromRoute(route);
}

/**
 * For pages that open a record in a Bootstrap sidebar/modal instead of its own page:
 *
 *   mixins: [chatContextMixin],
 *   getRequest(trf) { this.setChatContext("finance", trf.id, "#slideRightModalAll"); ... }
 *
 * The context is cleared when that modal closes and when the page is left.
 */
export const chatContextMixin = {
  methods: {
    setChatContext(type, id, modalSelector) {
      if (!type || !id) return;
      this.clearChatContext();
      const context = { type, id: Number(id) };
      chat.state.pageContext = context;
      this.$_chatContext = context;

      const $ = window.jQuery;
      if (modalSelector && $) {
        const el = $(modalSelector);
        const onHidden = () => this.clearChatContext();
        el.on("hidden.bs.modal", onHidden);
        this.$_chatModalUnbind = () => el.off("hidden.bs.modal", onHidden);
      }
    },
    /**
     * Clear the context this page set. Only its own: Vue creates the next page before
     * destroying this one, and the next page may already have set its own.
     */
    clearChatContext() {
      if (this.$_chatContext && chat.state.pageContext === this.$_chatContext) {
        chat.state.pageContext = null;
      }
      this.$_chatContext = null;
      this.$_unbindChatModal();
    },
    $_unbindChatModal() {
      if (this.$_chatModalUnbind) this.$_chatModalUnbind();
      this.$_chatModalUnbind = null;
    },
  },
  beforeDestroy() {
    this.clearChatContext();
  },
};

/** The ?focus=<id> a chat tag adds when it links to a list page, or null. */
export function focusId(route) {
  const id = route && route.query && route.query.focus;
  return id && /^\d+$/.test(String(id)) ? Number(id) : null;
}
